import { z } from 'zod';
import { Admission, AdmissionListQuery, DecideAdmissionBody } from '../schemas/admission.ts';
import {
  AcceptAccountInvitationBody,
  AccountInvitationCreated,
  AccountInvitationPreview,
  AccountPublic,
  CreateAccountInvitationBody,
  LoginBody,
  SessionView,
  UpdateAccountBody,
  UpdateMyAccountBody,
} from '../schemas/identity.ts';
import {
  AssetCreated,
  AssetDetail,
  AssetSummary,
  CreateAssetBody,
  Track,
  UpdateAssetBody,
  UpdateTrackBody,
  UploadSubtitleFields,
} from '../schemas/library.ts';
import { Id, PageQuery, pageOf } from '../schemas/primitives.ts';
import {
  ChatMessage,
  ChatPage,
  ChatPageQuery,
  MuteParticipantBody,
  PlaybackCommand,
  PlaybackState,
  PostChatBody,
  RemoveParticipantBody,
  RoomSnapshot,
  RtcCredentials,
  ServerTime,
} from '../schemas/room.ts';
import {
  CreateScreeningBody,
  InvitePreview,
  JoinInviteBody,
  JoinInviteResult,
  ScreeningListQuery,
  ScreeningSummary,
  UpdateScreeningBody,
} from '../schemas/screenings.ts';
import { defineRoute } from './define.ts';

const AssetParams = z.object({ assetId: Id });
const TrackParams = z.object({ assetId: Id, trackId: Id });
const ScreeningParams = z.object({ screeningId: Id });
const ParticipantParams = z.object({ screeningId: Id, identity: z.string().min(1).max(100) });
const AdmissionParams = z.object({ screeningId: Id, admissionId: Id });
const InviteParams = z.object({ code: z.string().min(10).max(64) });
const InvitationParams = z.object({ token: z.string().min(10).max(128) });
const AccountParams = z.object({ accountId: Id });

/** Every JSON endpoint of the API. The server registers handlers from this table; the client calls it. */
export const routes = {
  // ── Session & identity ────────────────────────────────────────────────
  getSession: defineRoute({
    method: 'GET',
    path: '/api/session',
    auth: 'public',
    response: SessionView,
    status: 200,
  }),
  getServerTime: defineRoute({
    method: 'GET',
    path: '/api/time',
    auth: 'public',
    response: ServerTime,
    status: 200,
  }),
  login: defineRoute({
    method: 'POST',
    path: '/api/auth/login',
    auth: 'public',
    body: LoginBody,
    response: AccountPublic,
    status: 200,
  }),
  logout: defineRoute({
    method: 'POST',
    path: '/api/auth/logout',
    auth: 'public',
    response: null,
    status: 204,
  }),
  getAccountInvitation: defineRoute({
    method: 'GET',
    path: '/api/account-invitations/:token',
    auth: 'public',
    params: InvitationParams,
    response: AccountInvitationPreview,
    status: 200,
  }),
  acceptAccountInvitation: defineRoute({
    method: 'POST',
    path: '/api/account-invitations/:token/accept',
    auth: 'public',
    params: InvitationParams,
    body: AcceptAccountInvitationBody,
    response: AccountPublic,
    status: 200,
  }),
  createAccountInvitation: defineRoute({
    method: 'POST',
    path: '/api/account-invitations',
    auth: 'owner',
    body: CreateAccountInvitationBody,
    response: AccountInvitationCreated,
    status: 201,
  }),
  /** The signed-in account changes its own name (shown to guests in rooms and chat). */
  updateMyAccount: defineRoute({
    method: 'PATCH',
    path: '/api/account',
    auth: 'account',
    body: UpdateMyAccountBody,
    response: AccountPublic,
    status: 200,
  }),
  listAccounts: defineRoute({
    method: 'GET',
    path: '/api/accounts',
    auth: 'owner',
    query: PageQuery,
    response: pageOf(AccountPublic),
    status: 200,
  }),
  updateAccount: defineRoute({
    method: 'PATCH',
    path: '/api/accounts/:accountId',
    auth: 'owner',
    params: AccountParams,
    body: UpdateAccountBody,
    response: AccountPublic,
    status: 200,
  }),

  // ── Library ───────────────────────────────────────────────────────────
  listAssets: defineRoute({
    method: 'GET',
    path: '/api/assets',
    auth: 'account',
    query: PageQuery,
    response: pageOf(AssetSummary),
    status: 200,
  }),
  createAsset: defineRoute({
    method: 'POST',
    path: '/api/assets',
    auth: 'account',
    body: CreateAssetBody,
    response: AssetCreated,
    status: 201,
  }),
  getAsset: defineRoute({
    method: 'GET',
    path: '/api/assets/:assetId',
    auth: 'account',
    params: AssetParams,
    response: AssetDetail,
    status: 200,
  }),
  updateAsset: defineRoute({
    method: 'PATCH',
    path: '/api/assets/:assetId',
    auth: 'account',
    params: AssetParams,
    body: UpdateAssetBody,
    response: AssetDetail,
    status: 200,
  }),
  deleteAsset: defineRoute({
    method: 'DELETE',
    path: '/api/assets/:assetId',
    auth: 'account',
    params: AssetParams,
    response: null,
    status: 204,
  }),
  uploadSubtitle: defineRoute({
    method: 'POST',
    path: '/api/assets/:assetId/subtitles',
    auth: 'account',
    params: AssetParams,
    multipart: UploadSubtitleFields,
    response: Track,
    status: 201,
  }),
  /** Multipart: the image in `file` (POSTER_UPLOAD_FORMATS). Replaces the poster everywhere. */
  uploadPoster: defineRoute({
    method: 'POST',
    path: '/api/assets/:assetId/poster',
    auth: 'account',
    params: AssetParams,
    multipart: z.object({}),
    response: AssetDetail,
    status: 200,
  }),
  /** Returns to the automatic frame from the film. */
  resetPoster: defineRoute({
    method: 'DELETE',
    path: '/api/assets/:assetId/poster',
    auth: 'account',
    params: AssetParams,
    response: AssetDetail,
    status: 200,
  }),
  updateTrack: defineRoute({
    method: 'PATCH',
    path: '/api/assets/:assetId/tracks/:trackId',
    auth: 'account',
    params: TrackParams,
    body: UpdateTrackBody,
    response: Track,
    status: 200,
  }),
  deleteTrack: defineRoute({
    method: 'DELETE',
    path: '/api/assets/:assetId/tracks/:trackId',
    auth: 'account',
    params: TrackParams,
    response: null,
    status: 204,
  }),

  // ── Screenings (studio) ───────────────────────────────────────────────
  listScreenings: defineRoute({
    method: 'GET',
    path: '/api/screenings',
    auth: 'account',
    query: PageQuery.extend(ScreeningListQuery.shape),
    response: pageOf(ScreeningSummary),
    status: 200,
  }),
  createScreening: defineRoute({
    method: 'POST',
    path: '/api/screenings',
    auth: 'account',
    body: CreateScreeningBody,
    response: ScreeningSummary,
    status: 201,
  }),
  getScreening: defineRoute({
    method: 'GET',
    path: '/api/screenings/:screeningId',
    auth: 'account',
    params: ScreeningParams,
    response: ScreeningSummary,
    status: 200,
  }),
  updateScreening: defineRoute({
    method: 'PATCH',
    path: '/api/screenings/:screeningId',
    auth: 'account',
    params: ScreeningParams,
    body: UpdateScreeningBody,
    response: ScreeningSummary,
    status: 200,
  }),
  endScreening: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/end',
    auth: 'account',
    params: ScreeningParams,
    response: ScreeningSummary,
    status: 200,
  }),
  rotateInvite: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/invite/rotate',
    auth: 'account',
    params: ScreeningParams,
    response: ScreeningSummary,
    status: 200,
  }),
  deleteScreening: defineRoute({
    method: 'DELETE',
    path: '/api/screenings/:screeningId',
    auth: 'account',
    params: ScreeningParams,
    response: null,
    status: 204,
  }),

  listAdmissions: defineRoute({
    method: 'GET',
    path: '/api/screenings/:screeningId/admissions',
    auth: 'account',
    params: ScreeningParams,
    query: AdmissionListQuery,
    response: pageOf(Admission),
    status: 200,
  }),
  decideAdmission: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/admissions/:admissionId',
    auth: 'account',
    params: AdmissionParams,
    body: DecideAdmissionBody,
    response: Admission,
    status: 200,
  }),

  // ── Invites (public entry) ────────────────────────────────────────────
  getInvite: defineRoute({
    method: 'GET',
    path: '/api/invites/:code',
    auth: 'public',
    params: InviteParams,
    response: InvitePreview,
    status: 200,
  }),
  joinInvite: defineRoute({
    method: 'POST',
    path: '/api/invites/:code/join',
    auth: 'public',
    params: InviteParams,
    body: JoinInviteBody,
    response: JoinInviteResult,
    status: 200,
  }),

  // ── Room ──────────────────────────────────────────────────────────────
  getRoom: defineRoute({
    method: 'GET',
    path: '/api/screenings/:screeningId/room',
    auth: 'session',
    params: ScreeningParams,
    response: RoomSnapshot,
    status: 200,
  }),
  issueRtcCredentials: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/rtc-credentials',
    auth: 'session',
    params: ScreeningParams,
    response: RtcCredentials,
    status: 200,
  }),
  commandPlayback: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/playback',
    auth: 'session',
    params: ScreeningParams,
    body: PlaybackCommand,
    response: PlaybackState,
    status: 200,
  }),
  listChat: defineRoute({
    method: 'GET',
    path: '/api/screenings/:screeningId/chat',
    auth: 'session',
    params: ScreeningParams,
    query: ChatPageQuery,
    response: ChatPage,
    status: 200,
  }),
  postChat: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/chat',
    auth: 'session',
    params: ScreeningParams,
    body: PostChatBody,
    response: ChatMessage,
    status: 201,
  }),
  muteParticipant: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/participants/:identity/mute',
    auth: 'session',
    params: ParticipantParams,
    body: MuteParticipantBody,
    response: null,
    status: 204,
  }),
  removeParticipant: defineRoute({
    method: 'POST',
    path: '/api/screenings/:screeningId/participants/:identity/remove',
    auth: 'session',
    params: ParticipantParams,
    body: RemoveParticipantBody,
    response: null,
    status: 204,
  }),
} as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

/**
 * Non-JSON endpoints. They live outside the route table because their bodies are streams or text,
 * but their paths are still part of the contract.
 */
export const RAW_ENDPOINTS = {
  /** Tus 1.0 resumable upload endpoint (creation, PATCH, HEAD). Account session required. */
  uploads: '/api/uploads',
  /** HLS master playlist generated from the asset's tracks. Requires the media grant cookie. */
  masterPlaylist: '/api/media/:assetId/master.m3u8',
  /** Poster for the invite preview; the invite code is the credential. */
  invitePoster: '/api/invites/:code/poster',
  /** nginx `auth_request` target for /media/*; never routed from outside. */
  mediaAuth: '/api/internal/media-auth',
  /** Static HLS segments, playlists, posters and subtitles served by nginx. */
  mediaFiles: '/media/:assetId/*',
  health: '/api/healthz',
  readiness: '/api/readyz',
  metrics: '/api/internal/metrics',
} as const;

/** Prefix that nginx never exposes publicly. */
export const INTERNAL_PATH_PREFIX = '/api/internal/';
